import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { FiltersOverlayDynamicService } from '@app/overlays/filters-overlay/services/filters-overlay-dynamic.service';
import { SearchSortBy } from '@app/shared/models/api/listing/search/search-sort-by.enum';
import { BehaviorSubject, Observable, Subject, Subscription, map, takeUntil } from 'rxjs';
import { PopsySkeletonComponent } from '../../skeleton/popsy-skeleton.component';
import { ArrowUp1IconComponent } from '../../../shared/components/icons/generated/arrow-up-1/arrow-up-1.component';
import { ArrowDown1IconComponent } from '../../../shared/components/icons/generated/arrow-down-1/arrow-down-1.component';
import { NgIf, AsyncPipe } from '@angular/common';
import { TranslocoModule } from '@ngneat/transloco';

@Component({
  selector: 'popsy-sort-by-filter',
  templateUrl: './sort-by-filter.template.html',
  styleUrls: ['./sort-by-filter.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoModule,
    NgIf,
    ArrowDown1IconComponent,
    ArrowUp1IconComponent,
    PopsySkeletonComponent,
    AsyncPipe,
  ],
})
export class SortByFilterComponent implements OnChanges, OnDestroy {
  @Input() public value?: SearchSortBy | null;
  @Input() public loading?: boolean | null;
  @Input() public compact?: boolean | null;

  @Output() public readonly sortByChanged: EventEmitter<SearchSortBy>;

  @ViewChild('sortFilterButton', { read: ElementRef })
  public sortFilterButtonRef?: ElementRef | null;

  private readonly sortBySubject: BehaviorSubject<SearchSortBy>;
  public readonly sortBy$: Observable<string>;

  private readonly openSubject: BehaviorSubject<boolean>;
  public readonly open$: Observable<boolean>;

  private readonly destroySubject: Subject<void>;

  private sortFilterOverlaySubscription?: Subscription | null;

  constructor(
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly filtersOverlayDynamicService: FiltersOverlayDynamicService
  ) {
    this.destroySubject = new Subject();
    this.sortByChanged = new EventEmitter();

    this.sortBySubject = new BehaviorSubject(SearchSortBy.NOT_SET as any);
    this.sortBy$ = this.sortBySubject
      .asObservable()
      .pipe(map(this.getSortFilter.bind(this)));

    this.openSubject = new BehaviorSubject(false as any);
    this.open$ = this.openSubject.asObservable();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.value) {
      this.sortBySubject.next(this.value || SearchSortBy.NOT_SET);
    }
  }

  public getSortFilter(option: SearchSortBy): string {
    switch (option) {
      case SearchSortBy.NOT_SET:
        return 'chips_sort_our_picks';
      case SearchSortBy.CREATED_AT_DESC:
        return 'chips_sort_newest';
      case SearchSortBy.PRICE_ASC:
        return 'chips_sort_lowest_price';
      case SearchSortBy.PRICE_DESC:
        return 'chips_sort_highest_price';
      case SearchSortBy.DISTANCE:
        return 'chips_sort_by_distance';
      case SearchSortBy.UPDATED:
        return 'chips_sort_updated';
      case SearchSortBy.VIEWS:
        return 'chips_sort_by_views';
      default:
        return 'chips_sort_any';
    }
  }

  public onOpenSortByFilters(): boolean {
    if (this.sortFilterOverlaySubscription) {
      this.sortFilterOverlaySubscription.unsubscribe();
    }

    this.sortFilterOverlaySubscription = this.filtersOverlayDynamicService.observe
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (data?: any | null) => {
          const isOpen = Boolean(data);
          this.openSubject.next(isOpen);

          if (!isOpen) {
            const sortBy =
              this.filtersOverlayDynamicService.sortBy || SearchSortBy.NOT_SET;
            this.sortBySubject.next(sortBy);
            this.sortByChanged.emit(sortBy);
          }
        },
      });

    const screenSize = this.akitaScreenQuery.size;
    this.filtersOverlayDynamicService.open({
      elementRef: this.compact ? null : this.sortFilterButtonRef,
      data: {
        mode: 'sort',
        sortBy: this.value || null,
        screenSize: screenSize,
        skipNavigation: true,
      },
      isBrowser: this.akitaRouterQuery.isBrowser,
    });

    return false;
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.sortFilterOverlaySubscription) {
      this.sortFilterOverlaySubscription.unsubscribe();
    }
  }
}
